import { type CanvasTransactionOrigin } from '@canvas/transaction';

import { type CommentThreads, type CommentThreadJSON } from './commentThreads.ts';
import { type Entry, type EntryJSON, type MutableEntry } from './entry.ts';
import { type Field, type FieldJSON, type MutableField } from './field.ts';
import { type DatabaseViewJSON, type MutableView, type View } from './views/view.ts';

export type DatabaseViewOrderJSON = {
  fieldOrder?: string[];
  entryOrder?: string[];
};

export type ViewOrderWriteBehavior =
  | 'activate-view-specific-ordering'
  | 'preserve-ordering-mode';

export function getOrderWithFallback(
  ids: readonly string[],
  preferredOrder: readonly string[] | undefined
): string[] {
  if (!preferredOrder) {
    return [...ids];
  }

  const remainingIds = new Set(ids);
  const result: string[] = [];

  preferredOrder.forEach((id) => {
    if (remainingIds.delete(id)) {
      result.push(id);
    }
  });

  ids.forEach((id) => {
    if (remainingIds.delete(id)) {
      result.push(id);
    }
  });

  return result;
}

/**
 * Places IDs at an index in the order that remains after those IDs are removed.
 */
export function placeIdsInOrder(
  order: readonly string[],
  ids: readonly string[],
  index: number
): string[] {
  const idsToPlace = [...new Set(ids)];
  const idsToPlaceSet = new Set(idsToPlace);
  const remainingIds = order.filter((id) => !idsToPlaceSet.has(id));
  const targetIndex = Math.min(remainingIds.length, Math.max(0, index));

  return [
    ...remainingIds.slice(0, targetIndex),
    ...idsToPlace,
    ...remainingIds.slice(targetIndex)
  ];
}

export function placeIdsRelativeToAnchor(
  order: readonly string[],
  ids: readonly string[],
  anchorId: string,
  position: 'before' | 'after'
): string[] {
  const idsToPlace = [...new Set(ids)];
  const idsToPlaceSet = new Set(idsToPlace);
  const remainingIds = order.filter((id) => !idsToPlaceSet.has(id));
  const anchorIndex = remainingIds.indexOf(anchorId);

  return placeIdsInOrder(
    remainingIds,
    idsToPlace,
    anchorIndex === -1
      ? remainingIds.length
      : anchorIndex + (position === 'after' ? 1 : 0)
  );
}

export function placeMappedIdsAfterSourceBlock(
  order: readonly string[],
  idsBySource: Readonly<Record<string, string>>
): string[] {
  const mappedIds = new Set(Object.values(idsBySource));
  const orderWithoutMappedIds = order.filter((id) => !mappedIds.has(id));
  const sourceIds = orderWithoutMappedIds.filter((id) =>
    Object.prototype.hasOwnProperty.call(idsBySource, id)
  );
  const lastSourceId = sourceIds[sourceIds.length - 1];

  if (lastSourceId === undefined) {
    return orderWithoutMappedIds;
  }

  return placeIdsInOrder(
    orderWithoutMappedIds,
    sourceIds.map((sourceId) => idsBySource[sourceId]),
    orderWithoutMappedIds.indexOf(lastSourceId) + 1
  );
}

export type DatabaseJSON = {
  fields: string[];
  fieldsById: Record<string, FieldJSON>;

  views: string[];
  viewsById: Record<string, DatabaseViewJSON>;
  viewOrders?: Record<string, DatabaseViewOrderJSON>;
  defaultViewId: string;

  entries: string[];
  entriesById: Record<string, EntryJSON>;
  defaultEntry: EntryJSON;

  commentThreads: Record<string, CommentThreadJSON[]>;
  isLocked: boolean | undefined;
};

export interface Database {
  readonly entries: Readonly<Array<string>>;
  readonly entriesById: Readonly<Record<string, Entry>>;
  get defaultEntry(): Entry;
  get fieldsById(): Readonly<Record<string, Field>>;
  get fields(): readonly string[];
  get views(): readonly string[];
  get viewsById(): Readonly<Record<string, View>>;
  get viewOrders(): Readonly<Record<string, DatabaseViewOrderJSON>>;
  get defaultViewId(): string;
  get isLocked(): boolean | undefined;
  get commentThreads(): Record<string, CommentThreads>;

  mutate(
    update: (database: MutableDatabase) => void,
    origin?: CanvasTransactionOrigin
  ): void;
  toJSON: () => DatabaseJSON;
}

export interface MutableDatabase extends Database {
  addEntry(id: string): MutableEntry;
  addEntryAt(id: string, index: number): MutableEntry;
  readonly entriesById: Readonly<Record<string, MutableEntry>>;
  deleteEntry(id: string): void;
  deleteEntries(ids: readonly string[]): void;

  addFieldFromJSON(fieldId: string, field: FieldJSON | FieldJSON[]): void;
  addFieldAt(fieldId: string, field: FieldJSON | FieldJSON[], index: number): void;
  get fieldsById(): Readonly<Record<string, MutableField>>;
  addView(viewId: string, view: DatabaseViewJSON): MutableView;
  setView(viewid: string, view: DatabaseViewJSON): void;
  deleteView(viewId: string): void;
  get viewsById(): Readonly<Record<string, MutableView>>;
  setViewFieldOrder(
    viewId: string,
    order: readonly string[],
    behavior?: ViewOrderWriteBehavior
  ): void;
  setViewEntryOrder(
    viewId: string,
    order: readonly string[],
    behavior?: ViewOrderWriteBehavior
  ): void;
  reorderView(viewId: string, newIndex: number): void;
  reorderField(fieldId: string, newIndex: number): void;
  reorderEntries(entryIds: string[], newIndex: number): void;
  deleteField(fieldId: string): void;
  deleteFields(fieldIds: readonly string[]): void;
  set defaultViewId(val: string);
  get defaultEntry(): MutableEntry;
  set isLocked(val: boolean | undefined);
  get commentThreads(): Record<string, CommentThreads>;
  addCommentThread(valueId: string, thread: CommentThreadJSON): CommentThreads;
  setCommentThread(valueId: string, thread: CommentThreadJSON): void;
  setCommentThreadResolved(valueId: string, threadId: string, resolved: boolean): void;
  removeCommentThread(valueId: string, threadId: string): void;
  removeCommentsOnValue(valueId: string): void;
  setCommentThreadCommentCount(
    valueId: string,
    threadId: string,
    commentCount: number
  ): void;
  incrementCommentThreadCommentCount(valueId: string, threadId: string): void;
  decrementCommentThreadCommentCount(valueId: string, threadId: string): void;
  incrementCommentThreadVersionNumber(valueId: string, threadId: string): void;
  reset(): void;
  merge(newDatabase: DatabaseJSON): void;
  replace(newDatabase: DatabaseJSON): void;
}
